import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as autoRedemptionViewActions from '@features/auto-redemption-settings-ui/actions/auto-redemption-settings-view.actions';
import {
  CreateCashBackAutoRedemptionSetting,
  CreateDigitalGiftCardAutoRedemptionSetting,
  CreatePhysicalGiftCardAutoRedemptionSetting,
  CreatePointsTransferAutoRedemptionSetting,
} from '@features/auto-redemption-settings/services/auto-redemption-setting-api/auto-redemption-setting-api.service';
import * as autoRedemptionSettingsActions from '@features/auto-redemption-settings/store/actions/auto-redemption-settings.actions';
import * as checkoutOrderActions from '@features/order-checkout/actions/order-checkout.actions';
import * as createOrderActions from '@features/order-checkout/actions/order-create.actions';
import * as promocodeActions from '@features/promocode/store/actions/promocode.actions';
import * as tcCheckoutActions from '@features/transfer-connect-v2/store/actions/checkout.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as actions from '@rd/core/mfa/store/actions/mfa.actions';
import {
  AutoRedemptionUpdate,
  MFAAutoRedemption,
  MFAAutoRedemptionProductType,
  MFAOneTimeRedemption,
  MFAOutcome,
  MFAProductType,
  MFARedemptionType,
  MFAShoppingCart,
  OneTimeRedemptionPointsTransfer,
} from '@rd/core/mfa/types';
import { Order } from '@rd/types';
import { AutoRedemptionSettingType } from '@rd/types/auto-redemption-setting.type';
import { isNotNull } from '@rd/utils/isNotNull';
import { combineLatest, Observable } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';

@Injectable()
export class MFARedirectEffects {
  constructor(private router: Router, private actions$: Actions) {}

  // auto redemption
  // Auto redemption update start
  hydrateAutoRedemptionUpdateAction$: Observable<autoRedemptionSettingsActions.HydrateAutoRedemptionSetting> =
    this.actions$.pipe(
      ofType(
        autoRedemptionSettingsActions.AutoRedemptionSettingTypes
          .HYDRATE_AUTO_REDEMPTION_SETTING,
      ),
    );

  restoreAutoRedemptionUpdateStateAction$ = this.actions$.pipe(
    ofType(actions.MFAActionTypes.RESTORE_STATE_FROM_MFA),
    filter(
      (payload: ReturnType<typeof actions.RestoreStateFromMFA>) =>
        payload.data.type === MFARedemptionType.AutoRedemption &&
        payload.data.product === MFAProductType.AutoRedemptionUpdate,
    ),
  );

  hydrateAutoRedemptionUpdate$ = createEffect(() =>
    this.restoreAutoRedemptionUpdateStateAction$.pipe(
      switchMap((otrData: ReturnType<typeof actions.RestoreStateFromMFA>) => {
        const finalActions = [];
        if (otrData.outcome === MFAOutcome.Success) {
          finalActions.push(
            new autoRedemptionSettingsActions.HydrateAutoRedemptionSetting({
              autoRedemptionUpdateHydrationState:
                otrData.data as AutoRedemptionUpdate,
              outcome: otrData.outcome,
            }),
          );
        }
        return finalActions;
      }),
    ),
  );

  hydrateAndUpdateAutoRedemptionSetting$ = createEffect(() =>
    this.hydrateAutoRedemptionUpdateAction$.pipe(
      filter(isNotNull),
      switchMap(
        (
          action: autoRedemptionSettingsActions.HydrateAutoRedemptionSetting,
        ) => {
          const { id, pointsRequired, strategy, strategyOptions } =
            action.payload.autoRedemptionUpdateHydrationState.data;
          const finalActions = [];
          if (action.payload.outcome === MFAOutcome.Success) {
            finalActions.push(
              new autoRedemptionSettingsActions.HydrateAndUpdateAutoRedemptionSetting(
                {
                  id,
                  pointsRequired,
                  strategy,
                  strategyOptions,
                },
              ),
            );
          }
          return finalActions;
        },
      ),
    ),
  );
  // Auto redemption update end

  restoreAutoRedemptionStateAction$ = this.actions$.pipe(
    ofType(actions.MFAActionTypes.RESTORE_STATE_FROM_MFA),
    filter(
      (payload: ReturnType<typeof actions.RestoreStateFromMFA>) =>
        payload.data.type === MFARedemptionType.AutoRedemption &&
        payload.data.product !== MFAProductType.AutoRedemptionUpdate,
    ),
  );

  initiateAR$ = createEffect(() =>
    this.restoreAutoRedemptionStateAction$.pipe(
      switchMap((a: ReturnType<typeof actions.RestoreStateFromMFA>) => {
        const finalActions = [];
        if (a.outcome === MFAOutcome.Success) {
          finalActions.push(
            autoRedemptionViewActions.AutoRedemptionSettingsViewEnterDetailsPage(
              {
                autoRedemptionSettingType: this.getAutoRedemptionSettingType(
                  (a.data as MFAAutoRedemption).product,
                ) as AutoRedemptionSettingType,
                isEverydaySpend: (a.data as MFAAutoRedemption).isEverydaySpend,
              },
            ),
          );
        }
        return finalActions;
      }),
    ),
  );

  hydrateArState$ = createEffect(() =>
    this.restoreAutoRedemptionStateAction$.pipe(
      switchMap((a: ReturnType<typeof actions.RestoreStateFromMFA>) => {
        const finalActions = [];

        const payloadData = a.data as MFAAutoRedemption;

        if (a.outcome === MFAOutcome.Success) {
          const arFormData = payloadData.data as
            | CreatePointsTransferAutoRedemptionSetting
            | CreateCashBackAutoRedemptionSetting
            | CreatePhysicalGiftCardAutoRedemptionSetting
            | CreateDigitalGiftCardAutoRedemptionSetting;

          finalActions.push(
            new autoRedemptionSettingsActions.SubmitAutoRedemptionSettingForm(
              arFormData,
              payloadData.isEverydaySpend,
            ),
          );

          finalActions.push(
            new autoRedemptionSettingsActions.CreateAutoRedemptionSetting(
              arFormData,
              payloadData.isEverydaySpend,
            ),
          );
        }
        return finalActions;
      }),
    ),
  );

  redirectToArReviewPage$ = createEffect(
    () =>
      combineLatest([
        this.actions$.pipe(
          ofType(
            autoRedemptionSettingsActions.AutoRedemptionSettingTypes
              .SUBMIT_AUTO_REDEMPTION_SETTING_FORM,
          ),
        ),
        this.restoreAutoRedemptionUpdateStateAction$,
      ]).pipe(
        tap(
          ([_, restoreStateAction]: [
            autoRedemptionSettingsActions.CreateAutoRedemptionSetting,
            ReturnType<typeof actions.RestoreStateFromMFA>,
          ]) => {
            if (restoreStateAction.outcome === MFAOutcome.Success) {
              this.router.navigateByUrl(
                restoreStateAction.data.verifiedRedirectUrl,
              );
            }
          },
        ),
      ),
    { dispatch: false },
  );

  // one time redemption points transfer

  hydratePointsTransferAction$: Observable<tcCheckoutActions.HydratePointsTransferRedemption> =
    this.actions$.pipe(
      ofType(
        tcCheckoutActions.CheckoutActionTypes
          .HYDRATE_POINTS_TRANSFER_REDEMPTION,
      ),
    );

  restoreOneTimeRedemptionPointsTransferStateAction$ = this.actions$.pipe(
    ofType(actions.MFAActionTypes.RESTORE_STATE_FROM_MFA),
    filter(
      (payload: ReturnType<typeof actions.RestoreStateFromMFA>) =>
        payload.data.type === MFARedemptionType.OneTimeRedemtion &&
        payload.data.product === MFAProductType.PointsTransfer,
    ),
  );

  hydrateOneTimeRedemptionPointsTransfer$ = createEffect(() =>
    this.restoreOneTimeRedemptionPointsTransferStateAction$.pipe(
      switchMap((otrData: ReturnType<typeof actions.RestoreStateFromMFA>) => {
        const finalActions = [];
        // this hydrates the store so that points transfer redeem page guard passes
        if (otrData.outcome === MFAOutcome.Success) {
          const pointsTransferHydrationState =
            otrData.data as OneTimeRedemptionPointsTransfer;

          finalActions.push(
            new tcCheckoutActions.HydratePointsTransferRedemption({
              pointsTransferHydrationState,
              outcome: otrData.outcome,
            }),
          );

          // If there is any promocode state
          // then we will hydrate it
          if (
            pointsTransferHydrationState.data.promocode &&
            pointsTransferHydrationState.data.promocodeUsage
          ) {
            finalActions.push(
              promocodeActions.HydratePromocode({
                promocode: {
                  code: pointsTransferHydrationState.data.promocode,
                },
                promocodeUsage:
                  pointsTransferHydrationState.data.promocodeUsage,
              }),
            );
          }
        }
        return finalActions;
      }),
    ),
  );

  initiatePointsTransferCheckout$ = createEffect(() =>
    this.hydratePointsTransferAction$.pipe(
      filter(isNotNull),
      switchMap((action: tcCheckoutActions.HydratePointsTransferRedemption) => {
        const {
          sourceAmount,
          targetAmount,
          membershipId,
          loyaltyProgramId,
          promocode,
        } = action.payload.pointsTransferHydrationState.data;
        const finalActions = [];
        if (action.payload.outcome === MFAOutcome.Success) {
          finalActions.push(
            new tcCheckoutActions.SubmitPointsTransfer({
              formData: {
                sourceAmount,
                targetAmount,
                membershipId,
              },
              loyaltyProgramId,
              promocode: promocode ? { code: promocode } : undefined,
            }),
          );
        }
        return finalActions;
      }),
    ),
  );

  redirectToPointsTransferRedeemPage$ = createEffect(
    () =>
      combineLatest([
        this.restoreOneTimeRedemptionPointsTransferStateAction$,
        this.hydratePointsTransferAction$,
      ]).pipe(
        filter((values) => values.every(isNotNull)),
        tap(([restoreState]) => {
          if (restoreState.outcome === MFAOutcome.Success) {
            this.router.navigateByUrl(restoreState.data.verifiedRedirectUrl);
          }
        }),
      ),
    { dispatch: false },
  );

  // shopping cart
  restoreStateAction$: Observable<
    {
      data: MFAAutoRedemption | MFAOneTimeRedemption | MFAShoppingCart;
      outcome: MFAOutcome;
    } & ReturnType<typeof actions.RestoreStateFromMFA>
  > = this.actions$.pipe(
    ofType(actions.MFAActionTypes.RESTORE_STATE_FROM_MFA),
    filter(
      (mfaActions: ReturnType<typeof actions.RestoreStateFromMFA>) =>
        mfaActions.data.type === MFARedemptionType.ShoppingCart,
    ),
  );

  hydrateOrderAction$: Observable<
    ReturnType<typeof createOrderActions.HydrateOrder>
  > = this.actions$.pipe(
    ofType(createOrderActions.CreateOrderActionTypes.HYDRATE_ORDER),
  );

  redirectToPaymentPage$ = createEffect(
    () =>
      combineLatest([this.restoreStateAction$, this.hydrateOrderAction$]).pipe(
        filter((values) => values.every(isNotNull)),
        tap(([restoreState]) => {
          if (restoreState.outcome === MFAOutcome.Success) {
            this.router.navigateByUrl(restoreState.data.verifiedRedirectUrl);
          }
        }),
      ),
    { dispatch: false },
  );

  handleShoppingCart$ = createEffect(() =>
    this.restoreStateAction$.pipe(
      switchMap((scData) => {
        const finalActions = [];
        if (scData.outcome === MFAOutcome.Success) {
          finalActions.push(
            createOrderActions.HydrateOrder({
              order: scData.data.data as Order,
              outcome: scData.outcome,
            }),
          );
        }
        return finalActions;
      }),
    ),
  );

  // Guard should now pass
  initiateOrderCheckout$ = createEffect(() =>
    this.hydrateOrderAction$.pipe(
      filter(isNotNull),
      switchMap((a) => {
        const finalActions = [];
        if (a.outcome === MFAOutcome.Success) {
          finalActions.push(checkoutOrderActions.CheckoutOrder());
        }
        return finalActions;
      }),
    ),
  );

  // utils

  private getAutoRedemptionSettingType(
    productType: Omit<
      MFAAutoRedemptionProductType,
      MFAProductType.AutoRedemptionUpdate
    >,
  ): AutoRedemptionSettingType | null {
    switch (productType) {
      case MFAProductType.PointsTransfer:
        return AutoRedemptionSettingType.PointsTransfer;
      case MFAProductType.DigitalGiftCard:
        return AutoRedemptionSettingType.DigitalGiftCard;
      case MFAProductType.PhysicalGiftCard:
        return AutoRedemptionSettingType.PhysicalGiftCard;
      case MFAProductType.Cashback:
        return AutoRedemptionSettingType.Cash;
    }
    return null;
  }
}
